import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useTranslation } from 'react-i18next';
import {
  LOGO,
  ONBOARDING_IMAGE,
  ONBOARDING_HEADLINE_KEY,
  ONBOARDING_SUBLINE_KEY,
  PRODUCT_NAME,
} from '../../branding';
import AuthBg from '../../assets/auth-background.svg';
import { redirectToAuthorize } from '../../auth/authService';
import { LanguagePicker } from '../../components/common/LanguagePicker';

/** One silent-SSO probe per browser tab (see the probe comment below). */
const SILENT_SSO_LATCH = 'svdy_silent_sso_tried';

const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  // Silent SSO probe (once per tab). A user who already signed in at svdy.com
  // (or in the sibling product) has an SVDY session cookie on id.svdy.com;
  // asking them to retype that email here was pure friction — the owner's
  // 2026-07-28 report ("从 profile 点击到 qutime 为何还要跳转一次 onboarding,
  // 输入已经在 svdy 中登录的邮箱"). prompt=none answers "already signed in?"
  // without rendering anything: on success the browser lands in the app; on
  // login_required / consent_required SSOCallback bounces back here and this
  // screen renders normally. The sessionStorage latch is what guarantees the
  // bounce cannot loop.
  const [probing, setProbing] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(SILENT_SSO_LATCH) !== '1';
    } catch {
      return false; // storage unavailable → never probe (a loop is worse)
    }
  });

  useEffect(() => {
    if (!probing) return;
    try {
      window.sessionStorage.setItem(SILENT_SSO_LATCH, '1');
    } catch {
      setProbing(false);
      return;
    }
    void redirectToAuthorize(undefined, { prompt: 'none' });
  }, [probing]);

  const validate = (value: string) => {
    if (!value.trim()) return t('auth:onboarding.validation.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
      return t('auth:onboarding.validation.emailInvalid');
    return '';
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate(email);
    if (err) { setEmailError(err); return; }
    const trimmed = email.trim();
    redirectToAuthorize(trimmed);
  };

  const handleSignUp = () => {
    navigate('/auth/signup');
  };

  // While the silent probe is in flight the browser is about to navigate to
  // the auth server; rendering the splash for those few frames would flash a
  // form the user never needs to touch.
  if (probing) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('auth:onboarding.checkingSession', { brand: PRODUCT_NAME })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundImage: `url(${AuthBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Logo */}
      <Box
        component="img"
        src={LOGO}
        alt={PRODUCT_NAME}
        sx={{ height: 32, mb: 2.5 }}
      />

      {/* Card — image left, form right */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          maxWidth: 820,
          mx: 2,
          border: '1px solid #d5d9d9',
          borderRadius: 2,
          bgcolor: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Left — image */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '50%',
            bgcolor: '#f9fbff',
            borderRight: '1px solid #eef1f5',
            p: 4,
          }}
        >
          <Box
            component="img"
            src={ONBOARDING_IMAGE}
            alt={PRODUCT_NAME}
            sx={{
              width: '100%',
              maxWidth: 360,
              height: 'auto',
              borderRadius: 2,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              mt: 2.5,
              color: 'text.primary',
              fontSize: '0.82rem',
              fontWeight: 600,
              textAlign: 'center',
              letterSpacing: '0.01em',
            }}
          >
            {t(ONBOARDING_HEADLINE_KEY)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              color: 'text.secondary',
              fontSize: '0.72rem',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {t(ONBOARDING_SUBLINE_KEY)}
          </Typography>
        </Box>

        {/* Right — form */}
        <Box sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column' }}>
          {/* Top-right actions: homepage link + language picker */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <LanguagePicker />
            <IconButton
              size="small"
              component="a"
              href="https://svdy.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'text.secondary' }}
              title={t('auth:onboarding.homeLinkTitle', { brand: PRODUCT_NAME })}
            >
              <HomeOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 2.5 }}
          >
            {t('auth:onboarding.signIn')}
          </Typography>

          <Box component="form" onSubmit={handleContinue} noValidate>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
              {t('auth:onboarding.emailLabel')}
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder={t('auth:onboarding.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(validate(e.target.value));
              }}
              error={Boolean(emailError)}
              helperText={emailError}
              autoFocus
              size="small"
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disableElevation
              sx={{
                borderRadius: 1,
                fontWeight: 600,
                py: 1,
                textTransform: 'none',
                fontSize: '0.9rem',
                bgcolor: '#1565c0',
                '&:hover': { bgcolor: '#0d47a1' },
              }}
            >
              {t('auth:onboarding.continue')}
            </Button>

            {/* Legal notice — directly under Continue button */}
            <Typography
              variant="caption"
              component="p"
              sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1, m: 0, mt: 1.5, p: 0, textAlign: 'center' }}
            >
              {t('auth:onboarding.legalPrefix')}
              <br />
              <Link href="https://svdy.com/terms" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1565c0', fontSize: '0.68rem' }}>{t('auth:onboarding.termsOfService')}</Link>
              {' '}{t('auth:onboarding.legalAnd')}{' '}
              <Link href="https://svdy.com/privacy" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: '#1565c0', fontSize: '0.68rem' }}>{t('auth:onboarding.privacyNotice')}</Link>.
            </Typography>
          </Box>

          <Divider sx={{ my: 2.5 }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
              {t('auth:onboarding.or')}
            </Typography>
          </Divider>

          <Button
            fullWidth
            variant="text"
            size="small"
            onClick={handleSignUp}
            sx={{
              textTransform: 'none',
              fontSize: '0.82rem',
              fontWeight: 500,
              color: '#1565c0',
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}
          >
            {t('auth:onboarding.createAccount')}
          </Button>

          {/* Copyright — pushed to bottom */}
          <Box sx={{ mt: 'auto', pt: 3, textAlign: 'center' }}>
            <Typography
              variant="caption"
              display="block"
              sx={{ color: 'text.disabled', fontSize: '0.64rem', letterSpacing: '0.02em' }}
            >
              {t('auth:onboarding.copyright')}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Onboarding;
